import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../redux/store';
import { toggleStaffConfidence } from '../../redux/slices/userProfile';
import { addSnackbar } from '../../redux/slices/snackbar';
import api, { clearApiCache } from '../../api/base';
import { roamOrganizations, roamSwitch } from '../../api/paths';
import { RoamOrganization } from './StaffWidget.types';
import { ReactComponent as SearchIcon } from '../../assets/icons/base/search.svg';
import { ReactComponent as XIcon } from '../../assets/icons/base/x.svg';
import { ReactComponent as CheckIcon } from '../../assets/icons/base/check.svg';
import { ReactComponent as ToolIcon } from '../../assets/icons/base/tool.svg';
import { CircleProgress } from '../common/CircleProgress';

const StaffWidget = () => {
  const [open, setOpen] = useState(false);
  const [orgs, setOrgs] = useState<RoamOrganization[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [teleportingId, setTeleportingId] = useState<string | null>(null);

  const dispatch = useDispatch();
  const user = useSelector((store: RootState) => store.user.user);
  const showConfidence = useSelector(
    (store: RootState) => store.user.staffShowConfidence,
  );

  const activeOrgId = user?.active_organization?.id;
  const orgName = user?.active_organization?.name || 'Staff Tools';

  const fetchOrgs = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<RoamOrganization[]>(roamOrganizations);
      setOrgs(data);
    } catch (err: any) {
      dispatch(addSnackbar({ message: 'Failed to load organizations' }));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open && orgs.length === 0) {
      fetchOrgs();
    }
  }, [open]);

  const handleTeleport = async (orgId: string) => {
    if (orgId === activeOrgId) return;
    setTeleportingId(orgId);
    try {
      await api.post(roamSwitch, { organization: orgId });
      clearApiCache();
      window.location.reload();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      dispatch(addSnackbar({ message: detail || 'Failed to switch organization' }));
      setTeleportingId(null);
    }
  };

  const filteredOrgs = searchQuery
    ? orgs.filter((o) => o.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : orgs;

  if (!user?.is_staff) return null;

  return (
    <>
      {/* Edge tab — hidden while the staff drawer is open */}
      <Box
        onClick={() => setOpen(true)}
        aria-label="Staff tools"
        role="button"
        tabIndex={0}
        sx={{
          position: 'fixed',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1300,
          writingMode: 'vertical-rl',
          cursor: 'pointer',
          bgcolor: '#25CEDE',
          color: 'grey.900',
          px: '4px',
          py: 1.5,
          borderRadius: '0 6px 6px 0',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.02em',
          boxShadow: 25,
          // The tab needs z-index above the sidebar to stay visible, which
          // also puts it above the drawer — so hide it while the drawer is
          // open rather than letting it overlap the drawer's list.
          display: open ? 'none' : 'flex',
          alignItems: 'center',
          gap: '6px',
          '& path': { stroke: 'currentColor' },
        }}
      >
        <ToolIcon style={{ width: 12, height: 12 }} />
        {orgName}
      </Box>

      {/* Slide-out drawer */}
      <Drawer
        anchor="left"
        open={open}
        onClose={() => { setOpen(false); setSearchQuery(''); }}
        PaperProps={{
          sx: {
            width: 360,
            bgcolor: 'base.background',
            boxShadow: 29,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="tsm"
            sx={{ fontWeight: 600, color: 'base.foreground' }}
          >
            Staff Tools
          </Typography>
          <IconButton
            aria-label="Close staff tools"
            size="small"
            onClick={() => { setOpen(false); setSearchQuery(''); }}
            sx={{ '& path': { stroke: (t) => t.palette.grey[500] } }}
          >
            <XIcon />
          </IconButton>
        </Box>

        {/* Organization Switcher — capped at 50% of drawer height */}
        <Box
          sx={{
            maxHeight: '50vh',
            display: 'flex',
            flexDirection: 'column',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Typography
              variant="txs"
              sx={{ fontWeight: 600, color: 'grey.500', mb: 1 }}
            >
              Switch Organization
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Search organizations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ width: 16, height: 16 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: 13,
                },
              }}
            />
          </Box>

          <List
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 1,
              pb: 1,
            }}
          >
            {loading && (
              <Box
                display="flex"
                justifyContent="center"
                py={3}
              >
                <CircleProgress />
              </Box>
            )}
            {!loading && filteredOrgs.length === 0 && (
              <Typography
                variant="txs"
                sx={{ color: 'grey.500', textAlign: 'center', py: 3 }}
              >
                {searchQuery ? 'No matching organizations' : 'No organizations available'}
              </Typography>
            )}
            {!loading && filteredOrgs.map((org) => {
              const isActive = org.id === activeOrgId;
              const isTeleporting = org.id === teleportingId;
              return (
                <ListItemButton
                  key={org.id}
                  onClick={() => handleTeleport(org.id)}
                  disabled={isTeleporting}
                  sx={{
                    borderRadius: '8px',
                    mb: 0.5,
                    py: 0.75,
                    bgcolor: isActive ? 'primary.100' : 'transparent',
                    '&:hover': {
                      bgcolor: isActive ? 'primary.100' : 'grey.100',
                    },
                  }}
                >
                  <ListItemText
                    primary={org.name}
                    primaryTypographyProps={{
                      variant: 'tsm',
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? 'primary.700' : 'text.primary',
                    }}
                  />
                  {isActive && (
                    <CheckIcon style={{ width: 16, height: 16 }} />
                  )}
                  {isTeleporting && <CircleProgress />}
                </ListItemButton>
              );
            })}
          </List>
        </Box>

        {/* Staff Tools section */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="tsm"
            sx={{ color: 'text.primary' }}
          >
            Confidence Scores
          </Typography>
          <Switch
            size="small"
            checked={showConfidence}
            onChange={() => dispatch(toggleStaffConfidence())}
          />
        </Box>
      </Drawer>
    </>
  );
};

export default StaffWidget;
